import React from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Container,
  MenuItem,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import GlassFormField from '@/components/design-studio/GlassFormField';
import { MarketingFormPanel } from '@/components/design-studio/GlassFormScrollPanel';
import { canSubmitRequiredForm } from '@/utils/required-fields';
import {
  FORM_PAGE_DESKTOP_BP,
  GLASS_FORM_PAIR_STACK_SPACING,
  GLASS_FORM_PANEL_PADDING_SX,
  GLASS_FORM_ROOT_SX,
  GLASS_FORM_ROW_SX,
  LIQUID_GLASS_FORM_SX,
  LIQUID_GLASS_PANEL_SX,
  LIQUID_GLASS_SUBMIT_BUTTON_SX,
} from '@/components/design-studio/design-studio-glass';
import HeaderLogo from '@/components/HeaderLogo';
import MarketingInteriorLayout from '@/components/home-landing/MarketingInteriorLayout';

export const EVENT_LEAD_FORM_GRID_SX = {
  width: '100%',
  maxWidth: { [FORM_PAGE_DESKTOP_BP]: 1200, xl: 1280 },
  mx: 'auto',
  px: { xs: 2, sm: 3, [FORM_PAGE_DESKTOP_BP]: 2, xl: 3 },
  gap: { xs: 4, [FORM_PAGE_DESKTOP_BP]: 3 },
};

const REQUIRED_FIELD_KEYS = ['first_name', 'last_name', 'phone_number', 'email_address', 'intent'];

function GlassFormSkeletonPanel() {
  return (
    <Box
      sx={{
        ...LIQUID_GLASS_PANEL_SX,
        ...GLASS_FORM_PANEL_PADDING_SX,
        width: '100%',
        maxWidth: 520,
      }}
    >
      <Stack spacing={1.75}>
        <Stack direction={{ xs: 'column', [FORM_PAGE_DESKTOP_BP]: 'row' }} spacing={1.5}>
          <Skeleton
            variant="rounded"
            height={48}
            sx={{ flex: 1, bgcolor: 'rgba(255,255,255,0.35)' }}
          />
          <Skeleton
            variant="rounded"
            height={48}
            sx={{ flex: 1, bgcolor: 'rgba(255,255,255,0.35)' }}
          />
        </Stack>
        {[1, 2, 3, 4, 5].map(key => (
          <Skeleton
            key={key}
            variant="rounded"
            height={48}
            sx={{ bgcolor: 'rgba(255,255,255,0.35)' }}
          />
        ))}
        <Skeleton variant="rounded" height={72} sx={{ bgcolor: 'rgba(255,255,255,0.35)' }} />
        <Skeleton
          variant="rounded"
          height={40}
          width={120}
          sx={{ alignSelf: 'flex-end', bgcolor: 'rgba(255,255,255,0.35)' }}
        />
      </Stack>
    </Box>
  );
}

export function EventLeadFormSkeleton() {
  return (
    <MarketingInteriorLayout heroBackground formGrid formGridSx={EVENT_LEAD_FORM_GRID_SX}>
      <Stack spacing={1.5}>
        <Skeleton
          variant="text"
          height={48}
          width="80%"
          sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}
        />
        <Skeleton
          variant="text"
          height={24}
          width="95%"
          sx={{ bgcolor: 'rgba(255,255,255,0.15)' }}
        />
        <Skeleton
          variant="text"
          height={24}
          width="70%"
          sx={{ bgcolor: 'rgba(255,255,255,0.15)' }}
        />
      </Stack>
      <Box
        sx={{
          width: '100%',
          minWidth: 0,
          display: 'flex',
          alignItems: { xs: 'stretch', [FORM_PAGE_DESKTOP_BP]: 'center' },
          justifyContent: { xs: 'stretch', [FORM_PAGE_DESKTOP_BP]: 'center' },
        }}
      >
        <GlassFormSkeletonPanel />
      </Box>
    </MarketingInteriorLayout>
  );
}

export function LeadCaptureForm({
  eventCode,
  eventName,
  logoSrc,
  intentOptions,
  variant = 'default',
  submitUrl = '/t/campaigns/public/leads/',
  formHeading = 'Event Contact Information',
  successRedirectUrl = 'https://niico.com',
}) {
  const isGlass = variant === 'glass';

  const defaultIntent = React.useMemo(() => {
    if (!intentOptions.length) return '';
    const other = intentOptions.find(o => o.slug === 'other');
    return other ? other.slug : intentOptions[0].slug;
  }, [intentOptions]);

  const [values, setValues] = React.useState({
    event_name: eventName || '',
    event_code: eventCode || '',
    first_name: '',
    last_name: '',
    company_name: '',
    phone_number: '',
    email_address: '',
    intent: defaultIntent || 'other',
    notes: '',
  });

  React.useEffect(() => {
    setValues(prev => ({
      ...prev,
      event_name: eventName || '',
      event_code: eventCode || '',
    }));
  }, [eventCode, eventName]);

  React.useEffect(() => {
    if (!intentOptions.length) return;
    const slugs = new Set(intentOptions.map(o => o.slug));
    setValues(prev => {
      if (slugs.has(prev.intent)) return prev;
      const other = intentOptions.find(o => o.slug === 'other');
      return { ...prev, intent: other ? other.slug : intentOptions[0].slug };
    });
  }, [intentOptions]);

  const [fieldErrors, setFieldErrors] = React.useState({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [snack, setSnack] = React.useState({
    open: false,
    message: '',
    severity: 'error',
  });

  const closeSnack = React.useCallback((event, reason) => {
    if (reason === 'clickaway') return;
    setSnack(prev => ({ ...prev, open: false }));
  }, []);

  const openErrorSnack = React.useCallback(message => {
    setSnack({
      open: true,
      message: message || 'Something went wrong.',
      severity: 'error',
    });
  }, []);

  const handleChange = React.useCallback(e => {
    const { name, value } = e.target;
    setValues(prev => ({ ...prev, [name]: value }));
  }, []);

  const canSubmit = React.useMemo(
    () => canSubmitRequiredForm(values, REQUIRED_FIELD_KEYS, ['email_address']),
    [values],
  );

  const handleSubmit = React.useCallback(
    async e => {
      e.preventDefault();

      setIsSubmitting(true);
      setFieldErrors({});

      try {
        const res = await fetch(submitUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(values),
        });

        if (!res.ok) {
          let payload = null;
          try {
            payload = await res.json();
          } catch {
            payload = null;
          }

          if (payload && typeof payload === 'object') {
            const nextFieldErrors = {};
            Object.keys(payload).forEach(key => {
              const v = payload[key];
              if (Array.isArray(v) && v.length) nextFieldErrors[key] = String(v[0]);
              else if (typeof v === 'string') nextFieldErrors[key] = v;
            });

            setFieldErrors(nextFieldErrors);

            const detailMsg = typeof payload.detail === 'string' ? payload.detail : '';
            openErrorSnack(detailMsg || 'Please fix the highlighted fields.');
          } else {
            openErrorSnack('Submission failed. Please try again.');
          }

          return;
        }

        window.location.assign(successRedirectUrl);
      } catch {
        openErrorSnack('Network error. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    },
    [values, openErrorSnack, submitUrl, successRedirectUrl],
  );

  const panelSx = isGlass
    ? { ...LIQUID_GLASS_PANEL_SX, ...GLASS_FORM_PANEL_PADDING_SX }
    : { p: 2.5, borderRadius: 2, border: 1, borderColor: 'divider' };

  const rowSx = isGlass ? GLASS_FORM_ROW_SX : { mb: 2 };
  const rowSpacing = isGlass ? GLASS_FORM_PAIR_STACK_SPACING : { xs: 0, [FORM_PAGE_DESKTOP_BP]: 2 };

  if (!intentOptions.length) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="warning">
          No intent options are configured for this event. Please contact the organizer.
        </Alert>
      </Container>
    );
  }

  const sectionHeadingSx = isGlass
    ? {
        color: 'rgba(255, 255, 255, 0.9)',
        fontSize: '0.75rem',
        fontWeight: 600,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        mb: { xs: 1, [FORM_PAGE_DESKTOP_BP]: 0.25 },
      }
    : { mb: 2 };

  const formHeader = (
    <Box sx={rowSx}>
      <Typography
        component="h2"
        variant={isGlass ? undefined : 'h6'}
        fontWeight={600}
        sx={sectionHeadingSx}
      >
        {formHeading}
      </Typography>
    </Box>
  );

  const formFooter = (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: isGlass ? 0.25 : 2 }}>
      <Button
        type="submit"
        variant="contained"
        size={isGlass ? 'medium' : 'large'}
        disableElevation
        disabled={isSubmitting || !canSubmit}
        sx={
          isGlass
            ? { ...LIQUID_GLASS_SUBMIT_BUTTON_SX, py: 1, minWidth: 120 }
            : { borderRadius: 2, py: 1.25 }
        }
      >
        {isSubmitting ? 'Submitting...' : 'Submit'}
      </Button>
    </Box>
  );

  const formFields = (
    <>
      <input type="hidden" name="event_name" value={values.event_name} readOnly />
      <input type="hidden" name="event_code" value={values.event_code} readOnly />
      <Stack
        direction={{ xs: 'column', [FORM_PAGE_DESKTOP_BP]: 'row' }}
        spacing={rowSpacing}
        sx={rowSx}
      >
        <GlassFormField
          isGlass={isGlass}
          name="first_name"
          label="First Name"
          value={values.first_name}
          onChange={handleChange}
          fullWidth
          required
          error={Boolean(fieldErrors.first_name)}
          helperText={fieldErrors.first_name}
          autoComplete="given-name"
        />
        <GlassFormField
          isGlass={isGlass}
          name="last_name"
          label="Last Name"
          value={values.last_name}
          onChange={handleChange}
          fullWidth
          required
          error={Boolean(fieldErrors.last_name)}
          helperText={fieldErrors.last_name}
          autoComplete="family-name"
        />
      </Stack>

      <Box sx={rowSx}>
        <GlassFormField
          isGlass={isGlass}
          name="company_name"
          label="Company Name (Optional)"
          value={values.company_name}
          onChange={handleChange}
          fullWidth
          error={Boolean(fieldErrors.company_name)}
          helperText={fieldErrors.company_name}
          autoComplete="organization"
        />
      </Box>

      <Box sx={rowSx}>
        <GlassFormField
          isGlass={isGlass}
          name="phone_number"
          label="Phone Number"
          value={values.phone_number}
          onChange={handleChange}
          fullWidth
          required
          error={Boolean(fieldErrors.phone_number)}
          helperText={fieldErrors.phone_number}
          autoComplete="tel"
        />
      </Box>

      <Box sx={rowSx}>
        <GlassFormField
          isGlass={isGlass}
          name="email_address"
          label="Email Address"
          type="email"
          value={values.email_address}
          onChange={handleChange}
          fullWidth
          required
          error={Boolean(fieldErrors.email_address)}
          helperText={fieldErrors.email_address}
          autoComplete="email"
        />
      </Box>

      <Box sx={rowSx}>
        <GlassFormField
          isGlass={isGlass}
          name="intent"
          label="What are you interested in?"
          value={values.intent}
          onChange={handleChange}
          fullWidth
          select
          error={Boolean(fieldErrors.intent)}
          helperText={fieldErrors.intent}
        >
          {intentOptions.map(opt => (
            <MenuItem key={opt.slug} value={opt.slug}>
              {opt.label}
            </MenuItem>
          ))}
        </GlassFormField>
      </Box>

      <Box sx={rowSx}>
        <GlassFormField
          isGlass={isGlass}
          name="notes"
          label="Notes (Optional)"
          value={values.notes}
          onChange={handleChange}
          fullWidth
          multiline
          maxRows={4}
          error={Boolean(fieldErrors.notes)}
          helperText={fieldErrors.notes}
          placeholder="Any details we should know?"
        />
      </Box>
    </>
  );

  const formStack = (
    <Stack
      component="form"
      onSubmit={handleSubmit}
      noValidate
      spacing={0}
      sx={isGlass ? LIQUID_GLASS_FORM_SX : undefined}
    >
      {formHeader}
      {formFields}
      {formFooter}
    </Stack>
  );

  const snackbar = (
    <Snackbar open={snack.open} autoHideDuration={6000} onClose={closeSnack}>
      <Alert onClose={closeSnack} severity={snack.severity} sx={{ width: '100%' }} variant="filled">
        {snack.message}
      </Alert>
    </Snackbar>
  );

  if (isGlass) {
    return (
      <Box sx={GLASS_FORM_ROOT_SX}>
        <MarketingFormPanel
          isGlass
          panelSx={panelSx}
          formSx={LIQUID_GLASS_FORM_SX}
          onSubmit={handleSubmit}
          header={formHeader}
          footer={formFooter}
        >
          {formFields}
        </MarketingFormPanel>
        {snackbar}
      </Box>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Stack spacing={2}>
        <HeaderLogo src={logoSrc} alt="NIICO" themeAware={false} />
        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            {eventName}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Share your details so our team can follow up about services, materials, or updates.
          </Typography>
        </Box>
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
          {formStack}
        </Paper>
        {snackbar}
      </Stack>
    </Container>
  );
}

LeadCaptureForm.propTypes = {
  eventCode: PropTypes.string.isRequired,
  eventName: PropTypes.string.isRequired,
  logoSrc: PropTypes.string,
  intentOptions: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  variant: PropTypes.oneOf(['default', 'glass']),
  submitUrl: PropTypes.string,
  formHeading: PropTypes.string,
  successRedirectUrl: PropTypes.string,
};
